import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { navigationService } from '@/common/navigationService';

/**
 * 공유 링크 → 앱에서 열기 (딥링크).
 *
 * 두 가지 형태를 모두 받는다.
 * 1. Universal Link / Android App Link — `https://www.myungchisa.com/<path>`
 *    (공유 시트·카페·메일에 그대로 나가는 링크. 앱이 없으면 웹이 열리므로 폴백이 필요 없다)
 * 2. 커스텀 스킴 — `myungchisa://open?path=<URL 인코딩된 경로>`
 *    (카카오톡 인앱 브라우저처럼 Universal Link 가 가로채이는 환경에서 웹 인터스티셜이 사용)
 *
 * 원격 URL 모드라 웹뷰는 이미 운영 사이트를 띄운 상태이므로, 문서를 새로 로드하지 않고
 * react-router 로만 이동한다(`navigationService`). 전체 리로드 시 흰 화면이 보이는 것을 막는다.
 */

/** 딥링크로 받아들일 우리 도메인. 그 외 호스트는 무시한다(오픈 리다이렉트 방지). */
const ALLOWED_HOSTS: readonly string[] = ['www.myungchisa.com', 'myungchisa.com'];

/** 커스텀 스킴 프로토콜 (`myungchisa:`). */
const CUSTOM_SCHEME: string = 'myungchisa:';

/**
 * react-router 가 아직 준비되기 전(콜드 스타트)에 도착한 딥링크를 잠시 보관한다.
 * `NavigationInitializer` 가 navigate 를 등록한 뒤 `flushPendingDeepLink()` 로 소비한다.
 */
let pendingPath: string | null = null;
let navigationReady: boolean = false;
let listenerAttached: boolean = false;

/**
 * 앱 내부 경로만 통과시킨다.
 * `//evil.com` 같은 프로토콜 상대 경로는 외부 이동이 되므로 거른다.
 */
function sanitizePath(path: string): string | null {
    if (!path.startsWith('/') || path.startsWith('//')) {
        return null;
    }
    return path;
}

/**
 * 딥링크 URL 을 앱 내부 경로(`/posts/123?x=1`)로 변환한다.
 *
 * @param rawUrl `appUrlOpen` 이 넘겨준 원본 URL
 * @returns 이동할 내부 경로. 우리 도메인/스킴이 아니거나 경로를 못 읽으면 `null`
 */
export function resolveDeepLinkPath(rawUrl: string): string | null {
    if (!rawUrl) {
        return null;
    }

    let url: URL;
    try {
        url = new URL(rawUrl);
    } catch (error) {
        console.debug('딥링크 URL 파싱 실패:', error);
        return null;
    }

    // 커스텀 스킴: myungchisa://open?path=%2Fsalary-surveys
    if (url.protocol === CUSTOM_SCHEME) {
        const encodedPath: string | null = url.searchParams.get('path');
        if (!encodedPath) {
            // path 없이 열린 경우(myungchisa://open)는 홈으로
            return '/';
        }
        // searchParams 가 이미 1회 디코드한다. 이중 인코딩된 링크도 살리기 위해 한 번 더 시도한다.
        let path: string = encodedPath;
        if (path.startsWith('%2F') || path.startsWith('%2f')) {
            try {
                path = decodeURIComponent(path);
            } catch (error) {
                console.debug('딥링크 path 디코드 실패:', error);
            }
        }
        return sanitizePath(path);
    }

    // Universal Link / App Link: https://www.myungchisa.com/<path>
    if ((url.protocol === 'https:' || url.protocol === 'http:') && ALLOWED_HOSTS.includes(url.hostname)) {
        return sanitizePath(`${url.pathname}${url.search}${url.hash}`);
    }

    return null;
}

/**
 * 결제 결과 페이지(토스가 돌려보낸 성공·실패 주소)에서는 딥링크 이동을 하지 않는다.
 * 여기서 다른 화면으로 옮기면 성공 페이지가 서버 승인(confirm)을 부르기 전에 사라져 결제가 승인되지 않는다
 * (2026-09-26 링크로 연 앱에서 카드 결제 후 세미나 화면으로 튕기고 결제가 안 된 사고).
 */
const PAYMENT_RESULT_PATH: RegExp = /\/payment\/(success|fail)(\/|$)/;

function isOnPaymentResultPage(): boolean {
    return PAYMENT_RESULT_PATH.test(window.location.pathname);
}

/**
 * 앱을 처음 연 링크(getLaunchUrl)는 앱 실행마다 한 번만 처리한다.
 * 원격 URL 앱은 결제 복귀처럼 문서가 통째로 다시 로드될 때마다 initNativeApp 이 다시 돌고,
 * getLaunchUrl 은 같은 URL 을 계속 돌려준다 — 그대로 두면 새로 로드될 때마다 처음 링크 화면으로 끌려간다.
 * sessionStorage 는 같은 웹뷰 안의 재로드에는 남고 앱을 새로 실행하면 비워진다.
 */
const LAUNCH_URL_HANDLED_KEY: string = 'mc_launch_url_handled';

function isLaunchUrlAlreadyHandled(url: string): boolean {
    try {
        if (sessionStorage.getItem(LAUNCH_URL_HANDLED_KEY) === url) return true;
        sessionStorage.setItem(LAUNCH_URL_HANDLED_KEY, url);
    } catch {
        // 저장소를 못 쓰면 예전처럼 처리한다(결제 결과 페이지 보호는 isOnPaymentResultPage 가 따로 한다)
    }
    return false;
}

/** 라우터가 준비됐으면 바로 이동하고, 아니면 준비될 때까지 보관한다. */
function routeTo(path: string): void {
    if (isOnPaymentResultPage()) {
        console.debug('결제 결과 페이지라 딥링크 이동을 건너뜀:', path);
        return;
    }
    if (navigationReady) {
        navigationService.navigateTo(path);
        return;
    }
    pendingPath = path;
}

/** 딥링크 URL 하나를 처리한다. 우리 링크가 아니면 아무것도 하지 않는다. */
function handleDeepLinkUrl(rawUrl: string): void {
    const path: string | null = resolveDeepLinkPath(rawUrl);
    if (path) {
        routeTo(path);
    }
}

/**
 * 딥링크 수신을 시작한다. `initNativeApp()` 에서 호출하며, 웹 브라우저에서는 아무것도 하지 않는다.
 *
 * - 앱이 이미 실행 중일 때 링크를 열면 `appUrlOpen` 이벤트로 들어온다.
 * - 앱이 꺼져 있다가 링크로 실행되면(콜드 스타트) `getLaunchUrl()` 로 최초 URL 을 읽는다.
 */
export function initDeepLinks(): void {
    if (!Capacitor.isNativePlatform() || listenerAttached) {
        return;
    }
    listenerAttached = true;

    void CapacitorApp.addListener('appUrlOpen', (event) => {
        handleDeepLinkUrl(event.url);
    });

    // 콜드 스타트: 링크로 앱이 처음 실행된 경우
    CapacitorApp.getLaunchUrl()
        .then((result) => {
            if (result?.url && !isLaunchUrlAlreadyHandled(result.url)) {
                handleDeepLinkUrl(result.url);
            }
        })
        .catch((error) => console.debug('getLaunchUrl 실패:', error));
}

/**
 * react-router 준비 완료를 알리고, 그 전에 도착해 대기 중이던 딥링크가 있으면 이동한다.
 * `App.tsx` 의 `NavigationInitializer` 에서 `navigationService.setNavigate()` 직후 호출한다.
 */
export function flushPendingDeepLink(): void {
    navigationReady = true;
    if (pendingPath) {
        const path: string = pendingPath;
        pendingPath = null;
        if (isOnPaymentResultPage()) {
            console.debug('결제 결과 페이지라 보관된 딥링크 이동을 건너뜀:', path);
            return;
        }
        navigationService.navigateTo(path);
    }
}

/**
 * 웹(모바일 브라우저)에서 앱을 열 때 쓰는 커스텀 스킴 URL 을 만든다.
 * 예: `/salary-surveys?x=1` → `myungchisa://open?path=%2Fsalary-surveys%3Fx%3D1`
 */
export function buildAppSchemeUrl(path: string): string {
    return `myungchisa://open?path=${encodeURIComponent(path)}`;
}
